import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getConnectorPrice } from "../utils/rateUtils";
import { buildStationPanel } from "../utils/stationPanel";
import analyticsService from "../services/analyticsService";
import ScreenLayout from "./ui/ScreenLayout";
import ConnectorCard from "./ui/ConnectorCard";
import LanguageSwitcher from "./ui/LanguageSwitcher";
import Button from "./ui/Button";

// "start"  : choix d'un connecteur libre pour lancer une nouvelle recharge
// "resume" : choix de la recharge en cours à reprendre (bornes multi-connecteurs)
const isConnectorSelectable = (connector, mode) =>
  mode === "resume"
    ? Boolean(connector.hasTransaction)
    : !connector.hasTransaction &&
      (connector.status === "Available" || connector.status === "Preparing");

const STEPS = [1, 2, 3];

const Steps = () => {
  const { t } = useTranslation();

  return (
    <ol className="mb-6 flex flex-col md:mb-7">
      {STEPS.map((step) => (
        <li key={step} className="flex gap-3.5">
          <div className="flex flex-col items-center">
            <span className="h-7 w-7 shrink-0 rounded-full bg-qv-brand text-white text-sm font-bold flex items-center justify-center">
              {step}
            </span>
            {step < STEPS.length && <span className="my-1 w-0.5 flex-1 bg-qv-brand-100" aria-hidden="true" />}
          </div>
          <div className={`pt-[3px] ${step < STEPS.length ? "pb-4" : ""}`}>
            <p className="text-[15px] leading-[22px] font-bold">{t(`landing.step${step}Title`)}</p>
            <p className="mt-0.5 text-[13px] leading-[19px] font-medium text-qv-muted md:text-sm md:leading-5">
              {t(`landing.step${step}Text`)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
};

// Écran d'arrivée (1a / 3a / 3b) : les 3 étapes, puis la prise et son tarif.
// En mode reprise, le même écran liste les recharges en cours à reprendre.
const ConnectorsComponent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { ref: reference } = useParams();
  const location = useLocation();
  const { evcpData, partner, connectorReference } = location.state || {};
  const mode = location.state?.mode === "resume" ? "resume" : "start";
  const isResumeFlow = mode === "resume";

  // Prises proposées. En démarrage, une prise en charge est masquée (4b) et une
  // prise visée par le QR code est seule à l'écran ; en reprise, seules les
  // recharges en cours comptent.
  const connectors = useMemo(() => {
    const all = (evcpData?.connectors || []).filter((c) => c.type !== "DOMESTIC_E");
    if (isResumeFlow) return all.filter((c) => c.hasTransaction);

    const free = all.filter((c) => !c.hasTransaction);
    const targeted = connectorReference && free.find((c) => c.reference === connectorReference);
    return targeted ? [targeted] : free;
  }, [evcpData, isResumeFlow, connectorReference]);

  const selectable = connectors.filter((c) => isConnectorSelectable(c, mode));

  // Présélection : la seule prise libre. Avec plusieurs prises libres (3a), ou
  // plusieurs recharges à reprendre, le client choisit.
  const [selectedId, setSelectedId] = useState(() =>
    !isResumeFlow && selectable.length === 1 ? selectable[0].id : null
  );

  useEffect(() => {
    if (!evcpData) {
      navigate("/error", { state: { errorText: t("noBorneData") } });
      return;
    }
    analyticsService.pushPageView("connector_selection", {
      station_id: evcpData.reference,
      mode,
    });
  }, [evcpData, navigate, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!evcpData) return null;

  // Une seule prise à proposer : « Votre prise ». Si une autre prise est en
  // charge et masquée (4b), elle reste présentée comme un choix déjà fait.
  const single = !isResumeFlow && connectors.length === 1;
  const hidesBusyConnector = (evcpData.connectors || []).some((c) => c.hasTransaction);
  const showAsChoice = !single || hidesBusyConnector;

  const handleSelect = (connector) => {
    setSelectedId(connector.id);
    if (!isResumeFlow) {
      analyticsService.pushEvent("connector_selected", {
        connector_id: connector.reference,
        connector_type: connector.type,
        power_kw: Math.floor(connector.powerMax),
      });
    }
  };

  const handleNext = () => {
    const selectedConnector = connectors.find((c) => c.id === selectedId);
    if (!selectedConnector) return;

    // Mode reprise : le connecteur choisi porte la recharge à reprendre.
    if (isResumeFlow) {
      analyticsService.pushEvent("active_session_detected", {
        station_id: evcpData.reference,
        connector_id: selectedConnector.reference,
      });
      navigate("/resume-session", {
        state: { evcpData, connector: selectedConnector, reference, partner },
      });
      return;
    }

    navigate("/email", {
      state: { connector: selectedConnector, evcpData, reference, partner },
    });
  };

  // Panneau de la version web : la prise choisie, sinon les prises proposées.
  const selectedConnector = connectors.find((c) => c.id === selectedId);
  const panel = buildStationPanel({
    t,
    language: i18n.language,
    variant: "station",
    evcpData,
    connector: isResumeFlow ? null : selectedConnector,
    connectors,
    withStatus: isResumeFlow,
  });

  const sectionTitle = isResumeFlow
    ? t("choose_connector_resume")
    : single
    ? t("landing.yourConnector")
    : t("landing.chooseConnector");

  return (
    <ScreenLayout
      onBack={isResumeFlow ? () => navigate(-1) : undefined}
      headerRight={<LanguageSwitcher />}
      step={isResumeFlow ? undefined : 1}
      panel={panel}
      footer={
        <Button onClick={handleNext} disabled={!selectedId}>
          {t("landing.continue")}
        </Button>
      }
    >
      {isResumeFlow ? (
        <>
          <h1 className="mb-2 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">
            {sectionTitle}
          </h1>
          <p className="mb-6 text-[15px] leading-6 text-qv-muted">{t("resumeList.subtitle")}</p>
        </>
      ) : (
        <>
          <h1 className="mb-1.5 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">
            {t("landing.title")}
          </h1>
          <p className="mb-5 text-sm leading-[21px] font-medium text-qv-muted md:mb-6 md:text-[15px] md:leading-[22px]">
            {t("landing.subtitle")}
          </p>
          <Steps />
          <p className="mb-2.5 text-xs font-semibold tracking-[.08em] uppercase text-qv-muted">{sectionTitle}</p>
        </>
      )}

      <div className="flex flex-col gap-3" role={showAsChoice ? "radiogroup" : undefined} aria-label={sectionTitle}>
        {connectors.map((connector) => {
          const canSelect = isConnectorSelectable(connector, mode);
          return (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              price={getConnectorPrice(connector, evcpData)}
              selected={showAsChoice && selectedId === connector.id}
              onSelect={showAsChoice ? handleSelect : undefined}
              disabled={!canSelect}
            />
          );
        })}
      </div>
    </ScreenLayout>
  );
};

export default ConnectorsComponent;
